import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { store } from "../../redux/oldStore";
import { ServerResponse } from '../../utils/Server';
import { FormatDate } from '../../utils/Tools';
import { IAutoAssignAudit, IAutoAssignRunSnapshot } from './JudgingCircles/JudgingCirlcesTypes';

const POOL_LABELS: Record<string, string> = {
    All: 'All',
    CheckedIn: 'Checked-in',
    Active: 'Active',
};

const MIX_LABELS: Record<string, string> = {
    MixedAvoid: 'Single category (mixed avoid)',
    Single: 'Single category (no mixed)',
    Mixed: 'Mixed categories',
};

interface IAutoAssignStartResult {
    status?: string;
    history?: IAutoAssignRunSnapshot[];
    error?: string;
}

interface IAutoAssignStatusResult {
    status?: string;
    assignments?: any;
    audit?: IAutoAssignAudit;
    history?: IAutoAssignRunSnapshot[];
    error?: string;
}

const JudgingRoundSetup = (props) => {
    const {
        judges
        , round
        , divisions
        , categories
        , projects
        , projectsByCategory
        , judgingSessions
    } = props
    const showAssignedCategoryColumn = !!props.Settings?.ShowAssignedCategoryColumn;
    const showCategoryPreferences1 = !!props.Settings?.ShowCategoryPreferences1;
    const showCategoryPreferences2 = !!props.Settings?.ShowCategoryPreferences2;
    const showCategoryPreferences3 = !!props.Settings?.ShowCategoryPreferences3;

    const [assignOptionPool, setAssignOptionPool] = useState('All');
    const [assignOptionSession, setAssignOptionSession] = useState('0');
    const coverageJudges = round?.Values?.IsCoverageJudging ? (round.Values.CoverageJudgesToAssign || round.Values.CoverageScoresToAccept || 5) : 0;
    const [assignOptionJudgeMin, setAssignOptionJudgeMin] = useState(coverageJudges || 3);
    const [assignOptionJudgeMax, setAssignOptionJudgeMax] = useState(coverageJudges || 5);
    const [division, setDivision] = useState(-1);
    const [maxNumberOfAssignmentsPerJudge, setMaxNumberOfAssignmentsPerJudge] = useState(15);
    const [judgeAssignment, setJudgeAssignment] = useState('MixedAvoid');
    const [judgeJoint, setJudgeJoint] = useState('separate');
    const [judgeGroupSize, setJudgesGroupSize] = useState(1);
    const [judgesAtATime, setJudgesAtATime] = useState(1);
    const [respectSessions, setRespectSessions] = useState(false);
    const [respectPreferences, setRespectPreferences] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [audit, setAudit] = useState<IAutoAssignAudit | null>(null);
    const [auditSearch, setAuditSearch] = useState('');
    const history: IAutoAssignRunSnapshot[] = props.autoAssignHistory ?? [];
    const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const assignDoneRef = useRef(false);

    const setHistory = (next: IAutoAssignRunSnapshot[]) => {
        if (typeof props.setAutoAssignHistory === 'function') {
            props.setAutoAssignHistory(next);
        }
    };

    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, []);

    const stopPolling = () => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    };

    useEffect(() => {
        if (!audit && history[0]?.Audit) {
            setAudit(history[0].Audit);
        }
        // history[0] identity is enough; do not depend on the array fallback.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history[0]?.Audit]);

    const applyFinishedRun = (status: IAutoAssignStatusResult) => {
        props.setAssignments(status.assignments);
        if (status.audit) {
            setAudit(status.audit);
            setAuditSearch('');
        }
        if (status.history) {
            setHistory(status.history);
        } else if (status.audit && history[0]) {
            setHistory([{ ...history[0], Audit: status.audit }, ...history.slice(1)]);
        }
        const shortfall = (status.audit?.ProjectsUnassigned ?? 0) + (status.audit?.ProjectsUnderMinimum ?? 0);
        if (shortfall > 0) {
            toast.warning(`${shortfall} project${shortfall === 1 ? '' : 's'} did not get the minimum number of judges. See the audit below.`);
        } else {
            toast.success('Judging assignments have been made.');
        }
    };

    const pollStatus = () => {
        store.server.getApiQuiet<ServerResponse<IAutoAssignStatusResult>>(`../JudgingManagement/AutoAssignRoundStatus?roundId=${round.Id}`)
            .then(res => {
                const status = res.Value?.status;
                if (status === 'done') {
                    stopPolling();
                    if (assignDoneRef.current) return;
                    assignDoneRef.current = true;
                    setIsAssigning(false);
                    applyFinishedRun(res.Value);
                } else if (status === 'error') {
                    stopPolling();
                    setIsAssigning(false);
                    toast.error(res.Value?.error || 'Something went wrong with the auto assign.');
                }
            })
            .catch(() => {
                stopPolling();
                setIsAssigning(false);
                toast.error('Lost connection while waiting for auto assign to finish.');
            });
    };

    const currentSnapshot = (): IAutoAssignRunSnapshot => ({
        RunAtUtc: new Date().toISOString(),
        JudgePool: assignOptionPool,
        JudgeSession: +assignOptionSession || 0,
        JudgeAssignment: judgeAssignment,
        JudgeTimesMin: assignOptionJudgeMin,
        JudgeTimesMax: assignOptionJudgeMax,
        MaxNumberOfAssignmentsPerJudge: maxNumberOfAssignmentsPerJudge,
        JudgeJointly: judgeJoint === 'same',
        Division: division,
        JudgesAtATime: judgesAtATime,
        JudgeGroupSize: judgeGroupSize,
        RespectSessions: respectSessions,
        RespectPreferences: respectPreferences,
    });

    const loadRun = (run: IAutoAssignRunSnapshot) => {
        setAssignOptionPool(run.JudgePool || 'All');
        setAssignOptionSession(String(run.JudgeSession ?? 0));
        setAssignOptionJudgeMin(run.JudgeTimesMin);
        setAssignOptionJudgeMax(run.JudgeTimesMax);
        setMaxNumberOfAssignmentsPerJudge(run.MaxNumberOfAssignmentsPerJudge);
        setJudgeAssignment(run.JudgeAssignment || 'MixedAvoid');
        setDivision(run.Division ?? -1);
        setJudgeJoint(run.JudgeJointly ? 'same' : 'separate');
        setJudgesGroupSize(run.JudgeGroupSize || 1);
        setJudgesAtATime(run.JudgesAtATime || 1);
        setRespectSessions(!!run.RespectSessions);
        setRespectPreferences(!!run.RespectPreferences);
        if (run.Audit) {
            setAudit(run.Audit);
            setAuditSearch('');
        }
        toast.success('Loaded settings from that run.');
    };

    const sessionName = (sessionId: number) => {
        if (!sessionId) return 'All sessions';
        return judgingSessions.find((x) => x.Id === sessionId)?.Name ?? `Session ${sessionId}`;
    };

    const divisionName = (divisionId?: number) => {
        if (divisionId == null || divisionId < 0) return 'All divisions';
        return divisions.find((x) => x.Id === divisionId)?.Name ?? `Division ${divisionId}`;
    };

    const summarizeRun = (run: IAutoAssignRunSnapshot) => {
        const parts = [
            `Pool: ${POOL_LABELS[run.JudgePool] ?? run.JudgePool}`,
            sessionName(run.JudgeSession),
            `Judges ${run.JudgeTimesMin}–${run.JudgeTimesMax} per project`,
            `Max ${run.MaxNumberOfAssignmentsPerJudge} projects / judge`,
            MIX_LABELS[run.JudgeAssignment] ?? run.JudgeAssignment,
            divisionName(run.Division),
        ];
        if (run.RespectPreferences) parts.push('Respect preferences');
        if (run.RespectSessions) parts.push('Respect sessions');
        if (run.JudgeGroupSize > 1) parts.push(`Group size ${run.JudgeGroupSize}`);
        if (run.JudgesAtATime > 1) parts.push(`${run.JudgesAtATime} judges at a time`);
        if (run.JudgeJointly) parts.push('Scheduled together');
        return parts.join(' · ');
    };

    const generate = () => {
        let request = {
            RoundId: round.Id,
            JudgePool: assignOptionPool,
            JudgeSession: assignOptionSession,
            JudgeTimesMin: assignOptionJudgeMin,
            JudgeTimesMax: assignOptionJudgeMax,
            JudgeAssignment: judgeAssignment,
            JudgeJointly: judgeJoint === "same",
            MaxNumberOfAssignmentsPerJudge: maxNumberOfAssignmentsPerJudge,
            Division: division,
            judgesAtATime,
            judgeGroupSize,
            respectSessions,
            respectPreferences
        }

        store.server.postApi<ServerResponse<IAutoAssignStartResult>>('../JudgingManagement/AutoAssignRound', request)
            .then(res => {
                if (res.Value?.status === 'started') {
                    assignDoneRef.current = false;
                    setIsAssigning(true);
                    if (res.Value.history) {
                        setHistory(res.Value.history);
                    } else {
                        setHistory([currentSnapshot(), ...history].slice(0, 5));
                    }
                    pollStatus();
                    pollIntervalRef.current = setInterval(pollStatus, 3000);
                } else {
                    toast.warning(res.Message || 'Could not start auto assign.');
                }
            })
            .catch(() => {
                toast.warning('Something went wrong starting the auto assign.');
            });
    }

    const clearRound = () => {
        if (!window.confirm(`Are you sure you want to clear all assignments from round "${round.Name}?"`)) {
            return;
        };

        store.server.postApi('../JudgingManagement/ClearRound', { roundId: round.Id })
            .then(res => {
                console.log(res)
                props.setAssignments([])

                toast.success("Judging assignments have been cleared.");
            })
            .catch(err => {
                console.error(err);
                toast.warning('something went wrong clearing the assignments')
            });
    }

    const mapSessions = judgingSessions.map((x, i) => {
        return (<option value={x.Id} key={i}>{x.Name}</option>);
    });


    const facingCategoryId = (categoryId: number) => {
        const match = (categories || []).find((c) => c.Id === categoryId);
        if (!match) return categoryId;
        if (match.IsParentCategory) return match.Id;
        if (match.ParentCategoryId) return match.ParentCategoryId;
        return match.Id;
    };
    const judgeFacingCategories = (categories || []).filter(
        (c) => c.IsParentCategory || !c.ParentCategoryId
    );
    const judgesAssignedToCategory = (categoryId: number) =>
        judges.filter((j) => facingCategoryId(j.AssignedCategory) === categoryId).length;

    const judgesFirstPref = (categoryId: number) =>
        judges.filter((j) => facingCategoryId(j.Category1) === categoryId).length;

    const judgesSecondPref = (categoryId: number) =>
        judges.filter((j) => facingCategoryId(j.Category2) === categoryId).length;

    const judgesThirdPref = (categoryId: number) =>
        judges.filter((j) => facingCategoryId(j.Category3) === categoryId).length;





    const latestRun = history[0];
    const isPeerReview = !!round?.Values?.IsPeerReview;

    return (
        <div>
            <div className="row">
                <div className="col-sm-8">
                    <div className="soft-border">
                        <h4>{isPeerReview ? 'Auto Assign Peer Review' : 'Auto Assign Judges'}</h4>
                        {isPeerReview && (
                            <p className="text-muted">
                                Participants are assigned to entries in other categories. They are never assigned their own entry.
                            </p>
                        )}
                        {latestRun && (
                            <p className="auto-assign-last-run">
                                Last run {FormatDate(latestRun.RunAtUtc, 'pretty')}
                                {' '}
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm"
                                    onClick={() => loadRun(latestRun)}
                                    disabled={isAssigning}
                                >
                                    Load last settings
                                </button>
                            </p>
                        )}
                        <hr />
                        <div className="head-room">
                            <div className="form-horizontal">
                                <div className="form-group">
                                    <label className="control-label col-sm-4" htmlFor="auto-judge-pool">{isPeerReview ? 'Participant Pool' : 'Judge Pool'}</label>
                                    <div className="col-sm-6">
                                        <select
                                            className="form-control"
                                            id="auto-judge-pool"
                                            onChange={(e) => setAssignOptionPool(e.target.value)}
                                            value={assignOptionPool}
                                        >
                                            <option value="All">All</option>
                                            <option value="CheckedIn">Checked-in</option>
                                            <option value="Active">Active (registered or signed in within the last 6 months)</option>
                                        </select>
                                    </div>
                                </div>
                                {!isPeerReview && <div className="form-group">
                                    <label className="control-label col-sm-4" htmlFor="auto-judge-session">Judge Session</label>
                                    <div className="col-sm-6">
                                        <select
                                            className="form-control"
                                            id="auto-judge-session"
                                            onChange={(e) => setAssignOptionSession(e.target.value)}
                                            value={assignOptionSession}
                                        >
                                            <option value="0">All</option>
                                            {mapSessions}
                                        </select>
                                    </div>
                                </div>}
                                {round?.Values?.IsCoverageJudging && <div className="form-group">
                                    <div className="col-sm-8 col-sm-offset-4">
                                        <p data-testid="coverage-assign-note">Coverage round: assign {round.Values.CoverageJudgesToAssign || coverageJudges} judges. Only {round.Values.CoverageScoresToAccept || 0} submitted scores count. No-shows and conflicts do not count.</p>
                                    </div>
                                </div>}
                                <div className="form-group">
                                    <label className="control-label col-sm-4" htmlFor="auto-judge-min">{isPeerReview ? 'Reviewers For Each Project' : 'Judges For Each Project'}</label>
                                    <div className="col-sm-5">
                                        <div className="input-group">
                                            <span className="input-group-addon">Min</span>
                                            <input
                                                id="auto-judge-min"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="50"
                                                className="form-control"
                                                onChange={(e) => setAssignOptionJudgeMin(e.target.valueAsNumber)}
                                                value={assignOptionJudgeMin}
                                            />
                                            <span className="input-group-addon">Max</span>
                                            <input
                                                id="auto-judge-max"
                                                type="number"
                                                step="1"
                                                min="0"
                                                max="50"
                                                className="form-control"
                                                onChange={(e) => setAssignOptionJudgeMax(e.target.valueAsNumber)}
                                                value={assignOptionJudgeMax}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label
                                        className="control-label col-sm-4"
                                        htmlFor="auto-judge-assign-max"
                                    >
                                        {isPeerReview ? 'Max Entries Per Reviewer' : 'Max Projects Per Judge'}
                                    </label>
                                    <div className="col-sm-3">
                                        <input
                                            id="auto-judge-assign-max"
                                            type="number"
                                            step="1"
                                            min="0"
                                            max="100"
                                            className="form-control"
                                            onChange={(e) => setMaxNumberOfAssignmentsPerJudge(e.target.valueAsNumber)}
                                            value={maxNumberOfAssignmentsPerJudge}
                                        />
                                    </div>
                                </div>
                                {!isPeerReview && <div className="form-group">
                                    <label className="control-label col-sm-4" htmlFor="auto-judge-mix">Judges Project Mix</label>
                                    <div className="col-sm-6">
                                        <select
                                            className="form-control"
                                            id="auto-judge-mix"
                                            onChange={(e) => setJudgeAssignment(e.target.value)}
                                            value={judgeAssignment}
                                        >
                                            <option value="MixedAvoid">Single category (mixed avoid)</option>
                                            <option value="Single">Single category (no mixed)</option>
                                            <option value="Mixed">Mixed categories</option>
                                        </select>
                                    </div>
                                </div>}
                                <div className="form-group">
                                    <label className="control-label col-sm-4" htmlFor="auto-division">Division</label>
                                    <div className="col-sm-6">
                                        <select
                                            className="form-control"
                                            id="auto-division"
                                            onChange={(e) => setDivision(+e.target.value)}
                                            value={division}
                                        >
                                            <option value="-1">all divisions</option>
                                            {divisions.map((x, i) => {
                        return (<option value={x.Id}>{x.Name}</option>);
                                            })}
                                        </select>
                                    </div>
                                </div>

                                {!isPeerReview && <>
                                <div className="form-group">
                                    <div className="col-sm-6 col-sm-offset-4">
                                        <input
                                            type="checkbox"
                                            id="auto-judge-respect-pref"
                                            onChange={(e) => setRespectPreferences(e.target.checked)}
                                            checked={respectPreferences}
                                        />
                                        <label className="control-label" htmlFor="auto-judge-respect-pref">Respect judges preferences</label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <div className="col-sm-6 col-sm-offset-4">
                                        <input
                                            type="checkbox"
                                            id="auto-judge-respect"
                                            onChange={(e) => setRespectSessions(e.target.checked)}
                                            checked={respectSessions}
                                        />
                                        <label className="control-label" htmlFor="auto-judge-respect">Respect judges sessions</label>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="control-label col-sm-7" htmlFor="auto-judge-group-size">Assign judges to a group of size</label>
                                    <div className="col-sm-5">
                                        <input
                                            id="auto-judge-group-size"
                                            type="number"
                                            step="1"
                                            min="1"
                                            max="20"
                                            style={{ width: '60px' }}
                                            className="form-control"
                                            onChange={(e) => setJudgesGroupSize(e.target.valueAsNumber)}
                                            value={judgeGroupSize}
                                        />
                                        <div className="text-muted">These judges will all be assiged the same projects and work together.
                                            Default is 1, judges will all be assigned their own list of assignments.
                                            Group size larger then 1 will only respect the category preferneces of one of the judges at random.
                                        </div>
                                    </div>
                                </div>
                                </>}


                                {props.round && props.round.Values && props.round.Values.SchedulesInterviews &&
                                    <>
                                        <div className="form-group hide">
                                            <label className="control-label col-sm-4" htmlFor="auto-judge-joint">Schedule</label>
                                            <div className="col-sm-6">
                                                <select
                                                    className="form-control"
                                                    id="auto-judge-joint"
                                                    onChange={(e) => setJudgeJoint(e.target.value)}
                                                    value={judgeJoint}
                                                >
                                                    <option value="separate">judges assigned to a project at separate times</option>
                                                    <option value="same">judges assiged to a project at the same time</option>
                                                </select>
                                            </div>
                                        </div>

                                        {/* <div className="form-group">
                  <div className="col-sm-6 col-sm-offset-4">
                    <input
                      type="checkbox"
                      id="auto-judge-respect"
                      onChange={(e) => setRespectSessions(e.target.checked)}
                      defaultChecked={respectSessions}
                    />
                  <label className="control-label" htmlFor="auto-judge-respect">Respect judges sessions</label>
                  </div>
                </div> */}

                                        {/* <div className="form-group">
                <label className="control-label col-sm-7" htmlFor="auto-judge-group-size">Assign judges to a group of size</label>
                <div className="col-sm-5">
                <input
                id="auto-judge-group-size"
                type="number"
                step="1"
                min="1"
                max="20"
                style={{width:'60px'}}
                className="form-control"
                onChange={(e) => setJudgesGroupSize(e.target.value)}
                value={judgeGroupSize}
              />
              <div className="text-muted">These judges will all be assiged the same projects and work together. 
              Default is 1, judges will all be assigned their own list of assignments.
              Group size larger then 1 will only respect the category preferneces of one of the judges at random.
              </div>
                </div>
              </div> */}

                                        <div className="form-group">
                                            <label className="control-label col-sm-7" htmlFor="auto-judge-at-a-time">Number of Judges with a project at a time.</label>
                                            <div className="col-sm-5">
                                                <input
                                                    id="auto-judge-at-a-time"
                                                    type="number"
                                                    step="1"
                                                    min="1"
                                                    max="10"
                                                    style={{ width: '60px' }}
                                                    className="form-control"
                                                    onChange={(e) => setJudgesAtATime(e.target.valueAsNumber)}
                                                    value={judgesAtATime}
                                                />
                                                <div className="text-muted">You can set how many judges you want with a project at a time. Default is 1, only one judge will be scheduled with a project at a time.</div>
                                            </div>
                                        </div>
                                    </>

                                }

                                <div className="form-group">
                                    <div className="head-room"></div>
                                    <div className="col-sm-8 col-sm-offset-4">
                                        <button
                                            type="button"
                                            className="btn btn-primary assign-judges-btn"
                                            data-testid="auto-assign-judges-btn"
                                            onClick={generate}
                                            disabled={isAssigning}
                                        >
                                            {isAssigning
                                                ? <><i className="fa fa-spinner fa-spin"></i> Assigning…</>
                                                : <><i className="fad fa-bomb"></i>{isPeerReview ? 'Assign Peer Reviews*' : 'Assign Judges*'}</>
                                            }
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-default clear-assignments-btn"
                                            onClick={clearRound}
                                            disabled={isAssigning}
                                        >
                                            <i className="text-danger fad fa-broom"></i>Clear assignments
                                        </button>
                                    </div>
                                    {isAssigning && (
                                        <div className="col-sm-8 col-sm-offset-4">
                                            <p className="text-info"><i className="fa fa-spinner fa-spin"></i> Auto assigning judges, this may take a minute…</p>
                                        </div>
                                    )}
                                    <div className="col-sm-8 col-sm-offset-4">
                                        <p>*Clicking this will clear the current assignments and start fresh</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-sm-4">
                    <div className="soft-border">
                        <h4>Previous runs</h4>
                        <p className="text-muted">Last 5 auto assign settings. Load one to put those values back in the form.</p>
                        {history.length === 0 && (
                            <p className="text-muted">No auto assign runs recorded yet.</p>
                        )}
                        {history.map((run, i) => (
                            <div
                                key={`${run.RunAtUtc}-${i}`}
                                className={`auto-assign-history-item${i === 0 ? ' is-latest' : ''}`}
                                data-testid="auto-assign-history-item"
                            >
                                <div className="run-when">
                                    {i === 0 ? 'Last run' : `Run ${i + 1}`} · {FormatDate(run.RunAtUtc, 'pretty')}
                                </div>
                                <div className="run-settings">{summarizeRun(run)}</div>
                                <button
                                    type="button"
                                    className="btn btn-default btn-sm"
                                    onClick={() => loadRun(run)}
                                    disabled={isAssigning}
                                >
                                    <i className="fad fa-undo"></i> Load these settings
                                </button>
                                {run.Audit && (
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm"
                                        data-testid="auto-assign-view-audit"
                                        onClick={() => { setAudit(run.Audit); setAuditSearch(''); }}
                                        disabled={isAssigning}
                                    >
                                        View audit
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {audit && <AutoAssignAuditPanel audit={audit} search={auditSearch} setSearch={setAuditSearch} />}
            <div className="soft-border head-room">
                <table className="table table-striped table-bordered">
                    <thead>
                        <tr>
                            <th>Categories</th>
                            <th>Number of Entries</th>
                            {showAssignedCategoryColumn && <th>Category Assignments</th>}
                            {showCategoryPreferences1 && <th>Judges 1st Preference</th>}
                            {showCategoryPreferences2 && <th>Judges 2nd Preference</th>}
                            {showCategoryPreferences3 && <th>Judges 3rd Preference</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {judgeFacingCategories.map((category, i) => (
                            <tr key={i}>
                                <td>{category.Name}</td>
                                <td>{projectsByCategory[category.Id]?.length ?? 0}</td>
                                {showAssignedCategoryColumn && <td>{judgesAssignedToCategory(category.Id)}</td>}
                                {showCategoryPreferences1 && <td>{judgesFirstPref(category.Id)}</td>}
                                {showCategoryPreferences2 && <td>{judgesSecondPref(category.Id)}</td>}
                                {showCategoryPreferences3 && <td>{judgesThirdPref(category.Id)}</td>}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

const AutoAssignAuditPanel = (props: {
    audit: IAutoAssignAudit;
    search: string;
    setSearch: (value: string) => void;
}) => {
    const { audit, search, setSearch } = props;
    const shortfall = (audit.ProjectsUnassigned ?? 0) + (audit.ProjectsUnderMinimum ?? 0);
    const query = search.trim().toLowerCase();
    const projects = (audit.Projects ?? []).filter((p) => {
        if (!query) return true;
        return (
            (p.ProjectId ?? '').toLowerCase().includes(query)
            || (p.Title ?? '').toLowerCase().includes(query)
            || (p.CategoryName ?? '').toLowerCase().includes(query)
            || (p.Reason ?? '').toLowerCase().includes(query)
        );
    });

    let summary: string;
    if ((audit.ProjectsInRound ?? 0) === 0) {
        summary = 'No projects were in this round to assign.';
    } else if (shortfall === 0) {
        summary = audit.ProjectsInRound === 1
            ? `The project was assigned at least ${audit.JudgeTimesMin} judge${audit.JudgeTimesMin === 1 ? '' : 's'}.`
            : `All ${audit.ProjectsInRound} projects were assigned at least ${audit.JudgeTimesMin} judges.`;
    } else {
        const bits = [];
        if (audit.ProjectsUnassigned) bits.push(`${audit.ProjectsUnassigned} with none`);
        if (audit.ProjectsUnderMinimum) bits.push(`${audit.ProjectsUnderMinimum} under the minimum`);
        summary = `${shortfall} of ${audit.ProjectsInRound} project${audit.ProjectsInRound === 1 ? '' : 's'} did not get the minimum of ${audit.JudgeTimesMin} judge${audit.JudgeTimesMin === 1 ? '' : 's'} (${bits.join(', ')}).`;
    }

    return (
        <div
            className={`auto-assign-audit ${shortfall > 0 || (audit.ProjectsInRound ?? 0) === 0 ? 'is-short' : 'is-ok'}`}
            data-testid="auto-assign-audit"
        >
            <h4>Assignment audit</h4>
            <p className="auto-assign-audit-summary" data-testid="auto-assign-audit-summary">
                {summary}
            </p>
            <p className="text-muted">
                {audit.ProjectsInRound} project{audit.ProjectsInRound === 1 ? '' : 's'} in this round
                {' · '}
                {audit.JudgesInPool} judge{audit.JudgesInPool === 1 ? '' : 's'} in the pool
                {' · '}
                {audit.SlotsNeeded} judge slots needed
                {' · '}
                {audit.SlotsAvailable} slots available
            </p>
            {audit.Notes?.length > 0 && (
                <ul className="auto-assign-audit-notes">
                    {audit.Notes.map((note, i) => (
                        <li key={i} data-testid="auto-assign-audit-note">{note}</li>
                    ))}
                </ul>
            )}
            {audit.Reasons?.length > 0 && (
                <ul className="auto-assign-audit-reasons">
                    {audit.Reasons.map((reason) => (
                        <li key={reason.ReasonCode} data-testid="auto-assign-audit-reason">
                            {reason.Reason} — {reason.Count}
                        </li>
                    ))}
                </ul>
            )}
            {shortfall > 0 && (
                <>
                    <div className="auto-assign-audit-search">
                        <input
                            type="search"
                            className="form-control"
                            data-testid="auto-assign-audit-search"
                            placeholder="Search unassigned projects"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search unassigned projects"
                        />
                    </div>
                    <div className="auto-assign-audit-table-wrap">
                        <table className="table table-striped table-bordered">
                            <thead>
                                <tr>
                                    <th>Project</th>
                                    <th>Category</th>
                                    <th>Judges</th>
                                    <th>Why</th>
                                </tr>
                            </thead>
                            <tbody>
                                {projects.map((p) => (
                                    <tr key={p.ProjectIntId} data-testid="auto-assign-audit-project">
                                        <td>
                                            <strong>{p.ProjectId}</strong>
                                            {p.Title ? <> {p.Title}</> : null}
                                        </td>
                                        <td>{p.CategoryName}</td>
                                        <td>{p.JudgeCount}/{p.JudgeTimesMin}</td>
                                        <td>{p.Reason}</td>
                                    </tr>
                                ))}
                                {projects.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="text-muted">No matching projects.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default JudgingRoundSetup